import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Select,
  Box,
  IconButton,
  FormHelperText,
  Card,
  Typography,
  TableContainer,
  Paper,
  Tooltip,
  Chip,
  InputAdornment,
} from "@material-ui/core";
import TablePagination from "@material-ui/core/TablePagination";
import { useFormik } from "formik";
import * as Yup from "yup";
import { makeStyles } from "@material-ui/core/styles";
import FilterPanel, { FilterSection } from '../../components/ui/FilterPanel';
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import FilterListIcon from "@material-ui/icons/FilterList";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SchoolIcon from "@material-ui/icons/School";

import SectionService from "./Locality/Service/sectionService";
import AddClassService from "./Locality/Service/addClassService";
import { showAppConfirm } from '../../context/AlertContext';

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: { padding: theme.spacing(1) },
  },
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: "1.9rem",
    color: "#1a1a1a",
    lineHeight: 1.2,
  },
  pageSubtitle: {
    color: "#555",
    fontSize: "0.9rem",
    marginTop: 2,
  },
  primaryBtn: {
    backgroundColor: "#1a1a1a",
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    padding: "8px 18px",
    boxShadow: "0 4px 10px rgba(23,105,214,0.25)",
    "&:hover": { backgroundColor: "#333" },
  },
  noteBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    backgroundColor: "#e6e6e6",
    border: "1px solid #d9d9d9",
    marginBottom: theme.spacing(2),
  },
  noteIconWrap: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "#1a1a1a",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  filtersCard: {
    borderRadius: 14,
    border: "1px solid #d9d9d9",
    boxShadow: "none",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filtersRow: {
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    width: "100%",
  },
  filtersChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 40,
    boxSizing: "border-box",
    padding: "0 12px",
    borderRadius: 8,
    border: "1px solid #8f8f8f",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontWeight: 600,
    color: "#1a1a1a",
    userSelect: "none",
    flexShrink: 0,
  },
  countBadge: {
    backgroundColor: "#fff",
    color: "#1a1a1a",
    border: "1px solid #c4c4c4",
    borderRadius: 12,
    padding: "1px 10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    marginLeft: 4,
  },
  fieldLabel: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#444",
    marginBottom: 6,
    display: "flex",
    alignItems: "center",
    gap: 4,
    minHeight: 18,
    lineHeight: "18px",
  },
  filterFieldWrap: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    minWidth: 150,
  },
  filterActionsWrap: {
    display: "flex",
    flexDirection: "column",
    flex: "0 0 auto",
    justifyContent: "flex-end",
  },
  resetBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    color: "#444",
    border: "1px solid #8f8f8f",
    height: 40,
    minWidth: 88,
    padding: "0 16px",
  },
  tableCard: {
    borderRadius: 14,
    border: "1px solid #d9d9d9",
    boxShadow: "none",
    overflow: "hidden",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #d9d9d9",
  },
  iconBtnOutlined: {
    border: "1px solid #d9d9d9",
    borderRadius: 8,
    padding: 7,
  },
  headCell: {
    backgroundColor: "#efefef",
    color: "#444",
    fontWeight: 700,
    fontSize: "0.8rem",
    borderBottom: "1px solid #d9d9d9",
    whiteSpace: "nowrap",
  },
  classChip: {
    backgroundColor: "#e6e6e6",
    color: "#1a1a1a",
    fontWeight: 600,
    height: 24,
  },
  sectionChip: {
    backgroundColor: "#f3efff",
    color: "#7c4dff",
    fontWeight: 600,
    height: 24,
  },
  statusActive: {
    backgroundColor: "#e6f7ed",
    color: "#1f8a5b",
    fontWeight: 600,
    height: 24,
  },
  statusInactive: {
    backgroundColor: "#efefef",
    color: "#8a8a8a",
    fontWeight: 600,
    height: 24,
  },
  iconAction: {
    padding: 6,
    color: "#444",
    "&:hover": { backgroundColor: "#f1f5fb", color: "#1a1a1a" },
  },
  deleteIcon: {
    padding: 6,
    color: "#444",
    "&:hover": { backgroundColor: "#ffebee", color: "#b3261e" },
  },
  dialogPaper: {
    borderRadius: 14,
    maxWidth: 420,
    width: "95%",
  },
  dialogTitleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2.5),
    borderBottom: "1px solid #d9d9d9",
    backgroundColor: "#fafafa",
  },
  dialogTitleText: {
    fontWeight: 700,
    fontSize: "1.15rem",
    color: "#1a1a1a",
  },
  dialogBody: {
    padding: theme.spacing(2.5),
  },
  fieldLabel: {
    display: "block",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#444",
    marginBottom: 6,
  },
  fieldWrap: {
    marginBottom: theme.spacing(2),
  },
  dialogFooter: {
    padding: theme.spacing(1.5, 2.5),
    borderTop: "1px solid #d9d9d9",
    backgroundColor: "#fafafa",
    gap: theme.spacing(1),
  },
  cancelBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    color: "#444",
  },
  saveBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    backgroundColor: "#1a1a1a",
    color: "#fff",
    boxShadow: "0 4px 10px rgba(23,105,214,0.25)",
    "&:hover": { backgroundColor: "#333" },
  },
}));

const tableHeaders = ["Class", "Section Name", "Status", "Actions"];

export default function Section() {
  const classes = useStyles();

  const [sectionList, setSectionList] = useState([]);
  const [classList, setClassList] = useState([]);
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState({
    classId: "",
    sectionName: "",
    status: true,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClassId, setFilterClassId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [pg, setPg] = useState(0);
  const [rpg, setRpg] = useState(10);

  useEffect(() => {
    getClassList();
  }, []);

  const getClassList = () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
    AddClassService.getAllAddClass(userDetails.schooleId)
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        const map = new Map();
        for (const item of list) {
          const key = String(item?.className || "").trim().toLowerCase();
          if (!key) continue;
          if (!map.has(key)) map.set(key, item);
        }
        const unique = Array.from(map.values()).sort((a, b) =>
          String(a?.className || "").localeCompare(String(b?.className || ""))
        );
        setClassList(unique);
      })
      .catch(() => setClassList([]));
  };

  const fetchFilteredSections = useCallback(() => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
    if (!userDetails.schooleId) return;

    if (filterClassId) {
      SectionService.findSectionList({ schooleId: userDetails.schooleId, classId: filterClassId })
        .then((res) => setSectionList(Array.isArray(res) ? res : []))
        .catch(() => setSectionList([]));
    } else {
      SectionService.getAllSection(userDetails.schooleId)
        .then((res) => setSectionList(Array.isArray(res) ? res : []))
        .catch(() => setSectionList([]));
    }
  }, [filterClassId]);

  useEffect(() => {
    setPg(0);
    fetchFilteredSections();
  }, [fetchFilteredSections]);

  const handleResetFilters = () => {
    setFilterClassId("");
    setFilterStatus("");
    setSearchTerm("");
    setPg(0);
  };

  const handleOpen = (reset = false) => {
    if (reset) {
      setSection({
        classId: "",
        sectionName: "",
        status: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const validationSchema = Yup.object().shape({
    classId: Yup.string().required("Class is required"),
    sectionName: Yup.string().required("Section Name is required"),
    status: Yup.boolean().required("Status is required"),
  });

  const editSection = (row) => {
    setSection({
      _id: row._id,
      classId: row.classId?._id || row.classId || "",
      sectionName: row.sectionName || "",
      status: row.status !== false,
    });
    setOpen(true);
  };

  const deleteSection = async (row) => {
    if (!row) return;
    if (await showAppConfirm("Are you sure you want to delete this section?")) {
      SectionService.deleteSection(row)
        .then(() => {
          fetchFilteredSections();
          alert("Section deleted successfully.");
        })
        .catch((err) => {
          alert(
            err?.response?.data?.message || "Failed to delete section. Please try again."
          );
        });
    }
  };

  const formik = useFormik({
    initialValues: section,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
      const payload = {
        schooleId: userDetails.schooleId,
        classId: values.classId,
        sectionName: values.sectionName,
        status: values.status,
      };

      if (section._id) {
        payload._id = section._id;
        SectionService.updateSection(payload)
          .then(() => {
            handleClose();
            fetchFilteredSections();
            resetForm();
            alert("Section updated successfully.");
          })
          .catch((err) => {
            alert(
              err?.response?.data?.message || "Failed to update section. Please try again."
            );
          });
      } else {
        SectionService.addSection(payload)
          .then(() => {
            fetchFilteredSections();
            handleClose();
            resetForm();
            alert("Section added successfully.");
          })
          .catch((err) => {
            alert(
              err?.response?.data?.message || "Failed to add section. Please try again."
            );
          });
      }
    },
  });

  const filteredList = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return sectionList.filter((row) => {
      if (filterStatus === "active" && row.status === false) return false;
      if (filterStatus === "inactive" && row.status !== false) return false;

      if (!term) return true;
      const cls = (row.classId?.className || "").toLowerCase();
      const name = (row.sectionName || "").toLowerCase();
      return cls.includes(term) || name.includes(term);
    });
  }, [sectionList, searchTerm, filterStatus]);

  const activeFilterCount = [filterClassId, filterStatus].filter(Boolean).length;

  return (
    <Box className={classes.pageWrapper}>
      <Box className={classes.pageHeader}>
        <Box>
          <Typography className={classes.pageTitle}>Section</Typography>
          {/* <Typography className={classes.pageSubtitle}>
            Manage class sections for your school. Sections are used when assigning students and teachers.
          </Typography> */}
        </Box>
        <Button
          variant="contained"
          className={classes.primaryBtn}
          startIcon={<AddIcon />}
          onClick={() => handleOpen(true)}
        >
          Add Section
        </Button>
      </Box>


      <Card elevation={0} className={classes.tableCard}>
        <Box className={classes.toolbar}>
          <Typography style={{ fontWeight: 700, color: "#1a1a1a" }}>
            {filteredList.length} Section{filteredList.length !== 1 ? "s" : ""}
          </Typography>
          <Box display="flex" alignItems="center" gridGap={10}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search sections…"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPg(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ fontSize: 18, color: "#8a8a8a" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterListIcon fontSize="small" />}
              onClick={() => setFiltersOpen((o) => !o)}
              aria-pressed={filtersOpen}
              style={filtersOpen ? { backgroundColor: '#e6e6e6' } : undefined}
            >
              Filters
              {activeFilterCount > 0 && <span className={classes.countBadge}>{activeFilterCount}</span>}
            </Button>
            <Tooltip title="Refresh">
              <IconButton className={classes.iconBtnOutlined} size="small" onClick={fetchFilteredSections}>
                <RefreshIcon style={{ fontSize: 20, color: "#444" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box display="flex" alignItems="stretch" flexWrap="wrap">
        <Box flex="1 1 520px" minWidth={0}>
        <TableContainer component={Paper} elevation={0} style={{ maxHeight: 520 }}>
          <Table stickyHeader size="small" className="dashboard-table-row-hover">
            <TableHead>
              <TableRow>
                {tableHeaders.map((key) => (
                  <TableCell key={key} className={classes.headCell}>{key}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length} align="center" style={{ padding: 48, color: "#8a8a8a" }}>
                    No sections found.
                  </TableCell>
                </TableRow>
              )}
              {filteredList.slice(pg * rpg, pg * rpg + rpg).map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    {row.classId?.className ? (
                      <Chip
                        size="small"
                        icon={<SchoolIcon style={{ fontSize: 14 }} />}
                        label={row.classId.className}
                        className={classes.classChip}
                      />
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={row.sectionName || "—"} className={classes.sectionChip} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status !== false ? "Active" : "Inactive"}
                      className={row.status !== false ? classes.statusActive : classes.statusInactive}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Tooltip title="Edit">
                        <IconButton className={classes.iconAction} size="small" onClick={() => editSection(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton className={classes.deleteIcon} size="small" onClick={() => deleteSection(row)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredList.length}
          page={pg}
          onPageChange={(_, p) => setPg(p)}
          rowsPerPage={rpg}
          onRowsPerPageChange={(e) => { setRpg(parseInt(e.target.value, 10)); setPg(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
        </Box>
                {filtersOpen && (
                    <FilterPanel onReset={handleResetFilters}>
                    <FilterSection title="Class">
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="filter-class-label">All Classes</InputLabel>
                  <Select
                    labelId="filter-class-label"
                    label="All Classes"
                    value={filterClassId}
                    onChange={(e) => setFilterClassId(e.target.value)}
                  >
                    <MenuItem value=""><em>All Classes</em></MenuItem>
                    {classList.map((c) => (
                      <MenuItem key={c._id} value={c._id}>{c.className}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                    </FilterSection>
                    <FilterSection title="Status">
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="filter-status-label">All Statuses</InputLabel>
                  <Select
                    labelId="filter-status-label"
                    label="All Statuses"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value=""><em>All Statuses</em></MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
                    </FilterSection>
                    </FilterPanel>
                )}
        </Box>
      </Card>

      <Dialog
        open={open}
        onClose={handleClose}
        scroll="paper"
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <Box className={classes.dialogTitleBar}>
          <Typography className={classes.dialogTitleText}>
            {section._id ? "Edit Section" : "Add Section"}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon style={{ fontSize: 20, color: "#8a8a8a" }} />
          </IconButton>
        </Box>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent className={classes.dialogBody}>
            <Box className={classes.fieldWrap}>
              <span className={classes.fieldLabel}>Class</span>
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                error={formik.touched.classId && Boolean(formik.errors.classId)}
              >
                <Select
                  id="classId"
                  name="classId"
                  displayEmpty
                  value={formik.values.classId}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="">
                    <em>Select Class</em>
                  </MenuItem>
                  {classList.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.className}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.classId && formik.errors.classId && (
                  <FormHelperText>{formik.errors.classId}</FormHelperText>
                )}
              </FormControl>
            </Box>

            <Box className={classes.fieldWrap}>
              <span className={classes.fieldLabel}>Section Name</span>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                autoFocus
                id="sectionName"
                name="sectionName"
                type="text"
                value={formik.values.sectionName}
                onChange={formik.handleChange}
                error={formik.touched.sectionName && Boolean(formik.errors.sectionName)}
                helperText={formik.touched.sectionName && formik.errors.sectionName}
              />
            </Box>

            <Box>
              <span className={classes.fieldLabel}>Status</span>
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                error={formik.touched.status && Boolean(formik.errors.status)}
              >
                <Select
                  id="status"
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions className={classes.dialogFooter}>
            <Button onClick={handleClose} className={classes.cancelBtn}>Cancel</Button>
            <Button type="submit" variant="contained" className={classes.saveBtn}>
              {section._id ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
